const LAST_SUCCESSFUL_CREDENTIAL_KEY = 'adam:lastSuccessfulCredential';
const REFRESH_EXPIRY_PENDING_KEY = 'adam:refreshExpiryCredentialPending';

const ADAM_LOCAL_STORAGE_KEYS = [
  'auth',
  'profile',
  'project',
  'asset-search-saved-filters',
  'savedAssetSearchFilters',
  'asset-tree-panel-width',
  'bom-table-split',
  'adam-home-dashboard-insights-tab',
] as const;

const ADAM_SESSION_STORAGE_KEYS = [
  'project',
  'assetTree',
  'assetHierarchy',
  'navigationIntent',
  'adam:initRedirectWarning',
] as const;

// 'assignment-ai:labels:' only clears legacy AI label caches (incl. its index) written by older builds; nothing writes them now.
const ADAM_LOCAL_STORAGE_PREFIXES = ['assetSupportGrid:', 'assignment-ai:labels:'] as const;
const ADAM_SESSION_STORAGE_PREFIXES = ['validation-test-'] as const;

function getLocalStorageSafely(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch (error: unknown) {
    console.warn('Unable to access browser localStorage for auth cleanup helpers.', error);
    return null;
  }
}

export function normalizeAuthCredential(credential: string): string {
  return credential.trim().toLowerCase();
}

export function rememberSuccessfulAuthCredential(credential: string): void {
  const normalizedCredential = normalizeAuthCredential(credential);
  if (!normalizedCredential) {
    return;
  }

  const storage = getLocalStorageSafely();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(LAST_SUCCESSFUL_CREDENTIAL_KEY, normalizedCredential);
  } catch (error: unknown) {
    console.warn('Failed to remember the last successful login credential.', error);
  }
}

export function markRefreshExpiryCredentialReconciliationPending(): void {
  const storage = getLocalStorageSafely();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(REFRESH_EXPIRY_PENDING_KEY, '1');
  } catch (error: unknown) {
    console.warn('Failed to mark refresh-expiry credential reconciliation as pending.', error);
  }
}

export function isRefreshExpiryCredentialReconciliationPending(): boolean {
  const storage = getLocalStorageSafely();
  if (!storage) {
    return false;
  }

  try {
    return storage.getItem(REFRESH_EXPIRY_PENDING_KEY) === '1';
  } catch (error: unknown) {
    console.warn('Failed to read refresh-expiry credential reconciliation marker.', error);
    return false;
  }
}

export function clearRefreshExpiryCredentialReconciliationPending(): void {
  const storage = getLocalStorageSafely();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(REFRESH_EXPIRY_PENDING_KEY);
  } catch (error: unknown) {
    console.warn('Failed to clear refresh-expiry credential reconciliation marker.', error);
  }
}

export function shouldPurgeStorageForCredentialMismatch(nextCredential: string): boolean {
  const storage = getLocalStorageSafely();
  if (!storage) {
    return false;
  }

  try {
    const isPending = storage.getItem(REFRESH_EXPIRY_PENDING_KEY) === '1';
    if (!isPending) {
      return false;
    }

    const previousCredential = storage.getItem(LAST_SUCCESSFUL_CREDENTIAL_KEY);
    const normalizedNextCredential = normalizeAuthCredential(nextCredential);
    if (!previousCredential || !normalizedNextCredential) {
      return true;
    }

    return previousCredential !== normalizedNextCredential;
  } catch (error: unknown) {
    console.warn('Failed to evaluate credential mismatch for storage purge.', error);
    return false;
  }
}

function collectMatchingKeys(storage: Storage, prefixes: readonly string[]): string[] {
  const matches: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key) {
      continue;
    }

    if (prefixes.some((prefix) => key.startsWith(prefix))) {
      matches.push(key);
    }
  }
  return matches;
}

function removeKnownEntries(
  storage: Storage,
  exactKeys: readonly string[],
  keyPrefixes: readonly string[],
  storageName: string,
): void {
  try {
    const keysToRemove = new Set<string>(exactKeys);
    const prefixedKeys = collectMatchingKeys(storage, keyPrefixes);
    prefixedKeys.forEach((key) => keysToRemove.add(key));
    keysToRemove.forEach((key) => storage.removeItem(key));
  } catch (error: unknown) {
    console.warn(`Failed to clear ADAM Cloud ${storageName} storage entries.`, error);
  }
}

export function purgeAdamCloudStorage(): void {
  if (typeof window === 'undefined') {
    return;
  }

  removeKnownEntries(
    window.localStorage,
    ADAM_LOCAL_STORAGE_KEYS,
    ADAM_LOCAL_STORAGE_PREFIXES,
    'local',
  );
  removeKnownEntries(
    window.sessionStorage,
    ADAM_SESSION_STORAGE_KEYS,
    ADAM_SESSION_STORAGE_PREFIXES,
    'session',
  );
}
